// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { BundleSortProperty, bundleSchema } from '@/bundle/api/types';
import { SortDirection } from '@/constants/sort';
import { z } from 'zod';

/**
 * Schema for the bundles list API response.
 * Transforms the envelope response into a flat { results, totalCount } shape.
 */
const bundlesResponseSchema = z
  .object({
    data: z.object({
      results: z.array(bundleSchema),
      totalCount: z.number(),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    results: data.results,
    totalCount: data.totalCount,
  }));

type BundlesResponse = z.infer<typeof bundlesResponseSchema>;

/**
 * Filter parameters for the bundles list.
 */
export interface BundlesFilter {
  query?: string;
  archived?: boolean;
  sort?: BundleSortProperty;
  direction?: SortDirection;
  page?: number;
  limit?: number;
}

/**
 * Fetches bundles from the catalog API with optional server-side searching,
 * sorting, and pagination.
 */
export async function getBundles(
  filter?: BundlesFilter
): Promise<BundlesResponse> {
  const url = '/v1/catalog/bundle';

  const response = await api.get(url, {
    params: {
      search: filter?.query,
      // When archived=true, include ALL bundles (archived + non-archived);
      // the backend defaults to non-archived only when the param is absent
      archived: filter?.archived ? 'all' : undefined,
      sortBy: filter?.sort,
      sortOrder: filter?.direction,
      page: filter?.page,
      limit: filter?.limit,
    },
  });

  return parseResponse(bundlesResponseSchema, response.data, {
    schemaName: 'Bundles',
    endpoint: url,
  });
}
